/**
 * "Start your free 14-day trial": the AgencyAnalytics sign-up, with Syntera's
 * rules — agency name, password rules typed twice, the human check.
 */

import { useState, type FormEvent, type ReactNode } from 'react';

import { ApiError } from '../api.ts';
import { HumanCheck } from '../login/HumanCheck.tsx';
import { LoginShell, PASSWORD_RULES, PasswordChecklist } from '../login/LoginShell.tsx';
import { startTrial } from './signup-api.ts';

export function SignUp({
  onDone,
  onSignIn,
}: {
  onDone: (email: string) => void;
  onSignIn: () => void;
}): ReactNode {
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [agency, setAgency] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [human, setHuman] = useState('');
  const [humanReset, setHumanReset] = useState(0);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const ready =
    Boolean(first.trim() && agency.trim() && email.trim()) &&
    PASSWORD_RULES.every((rule) => rule.test(password)) &&
    password === confirm;
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!ready) return;
    setBusy(true);
    setProblem(null);
    try {
      await startTrial({
        first_name: first.trim(),
        last_name: last.trim(),
        agency_name: agency.trim(),
        email: email.trim(),
        password,
        turnstile_token: human,
      });
      onDone(email.trim());
    } catch (cause) {
      setProblem(
        cause instanceof ApiError ? cause.message : 'Your trial could not be started. Try again.',
      );
      setHumanReset((count) => count + 1);
    } finally {
      setBusy(false);
    }
  };

  return (
    <LoginShell>
      <header className="li-head">
        <h1>Start your free 14-day trial</h1>
        <p>No credit card required.</p>
      </header>
      {problem && (
        <div className="li-error" role="alert">
          {problem}
        </div>
      )}
      <form className="li-form" onSubmit={(event) => void submit(event)}>
        <label htmlFor="su-first">First name</label>
        <input
          id="su-first"
          value={first}
          onChange={(event) => setFirst(event.target.value)}
          autoComplete="given-name"
          required
          autoFocus
        />
        <label htmlFor="su-last">Last name</label>
        <input
          id="su-last"
          value={last}
          onChange={(event) => setLast(event.target.value)}
          autoComplete="family-name"
        />
        <label htmlFor="su-agency">Agency name</label>
        <input
          id="su-agency"
          value={agency}
          onChange={(event) => setAgency(event.target.value)}
          autoComplete="organization"
          required
        />
        <label htmlFor="su-email">Work email</label>
        <input
          id="su-email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="email"
          placeholder="you@agency.co.za"
          required
        />
        <label htmlFor="su-password">Password</label>
        <input
          id="su-password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
          required
        />
        <label htmlFor="su-confirm">Retype password</label>
        <input
          id="su-confirm"
          type="password"
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          autoComplete="new-password"
          required
        />
        <PasswordChecklist value={password} confirm={confirm} />
        <HumanCheck onToken={setHuman} resetKey={humanReset} />
        <button className="li-submit" type="submit" disabled={busy || !ready || !human}>
          {busy ? 'Creating your workspace…' : 'Create account →'}
        </button>
        <p className="li-after">
          Already have an account?{' '}
          <button type="button" className="li-link" onClick={onSignIn}>
            Log in
          </button>
        </p>
        <p className="li-after li-legal">
          By creating an account you agree to the{' '}
          <a href="/terms" target="_blank" rel="noreferrer">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="/privacy" target="_blank" rel="noreferrer">
            Privacy Policy
          </a>
          .
        </p>
      </form>
    </LoginShell>
  );
}
